import { z } from "zod";
import { Id } from "./ids.js";

/** The Space zone every bot reads: what the company is, does and wants. */
export const COMPANY_ZONE_NAME = "Company";

/**
 * What a company fact is about. Each topic is one note in its zone, so facts on the same subject
 * sit together and a bot reads them as one document.
 */
export const FACT_TOPICS = [
  "profile",
  "goals",
  "services",
  "clients",
  "suppliers",
  "deadlines",
  "sites",
  "regions",
  "insurance",
  "accreditations",
  "team",
  "process",
  "contact",
  "tone",
  "lessons",
  "other",
] as const;
export const FactTopicSchema = z.enum(FACT_TOPICS);
export type FactTopic = z.infer<typeof FactTopicSchema>;

/** The note a topic's facts live in. */
export const FACT_TOPIC_PATHS: Record<FactTopic, string> = {
  profile: "Profile.md",
  goals: "Goals.md",
  services: "Services.md",
  clients: "Clients.md",
  suppliers: "Suppliers.md",
  deadlines: "Deadlines.md",
  sites: "Sites.md",
  regions: "Regions.md",
  insurance: "Insurance.md",
  accreditations: "Accreditations.md",
  team: "Team.md",
  process: "Process.md",
  contact: "Contact.md",
  tone: "Tone of voice.md",
  lessons: "Lessons.md",
  other: "Facts.md",
};

/** Where a proposed fact came from, so the owner can judge it. */
export const FACT_SOURCES = ["website", "document", "chat", "setup", "register"] as const;
export const FactSourceSchema = z.enum(FACT_SOURCES);
export type FactSource = z.infer<typeof FactSourceSchema>;

export const FACT_TEXT_MAX_LENGTH = 500;
export const FACTS_PER_PROPOSAL_MAX = 30;

/**
 * How a bot writes a proposed fact. Facts are often taken from what the owner typed, typos and
 * all, and every bot reads them as written once approved.
 */
export const FACT_TEXT_GUIDANCE =
  "Write each fact as one clean, complete sentence in plain English: correct the spelling, typos, capitalisation and grammar of what you read or the owner typed, without changing its meaning. Keep names, figures and dates exact, and never add claims the source does not make.";

const optionalLine = (max: number) => z.string().trim().max(max).optional();

/**
 * One person in the Space's team directory: who they are, what they do and how to reach them at
 * work. Bots name people from it instead of a generic role.
 */
export const DirectoryPersonSchema = z.object({
  name: z.string().trim().min(1).max(120),
  /** Job title or role, such as "Operations Manager". */
  role: optionalLine(120),
  department: optionalLine(80),
  /** Work mailboxes, personal or shared, that this person reads. */
  emails: z.array(z.string().trim().email().max(200)).max(5).optional(),
  /** Phone number or extension. */
  phone: optionalLine(40),
  /** The name of the person they report to. */
  reportsTo: optionalLine(120),
  notes: optionalLine(300),
});
export type DirectoryPerson = z.infer<typeof DirectoryPersonSchema>;

export const DIRECTORY_MAX_ENTRIES = 200;

export const ProposedFactSchema = z.object({
  topic: FactTopicSchema,
  text: z.string().trim().min(1).max(FACT_TEXT_MAX_LENGTH).describe(FACT_TEXT_GUIDANCE),
  /**
   * A team member's directory entry, with topic "team". Approving it adds or updates the
   * person in the team directory instead of a note.
   */
  person: DirectoryPersonSchema.optional(),
});
export type ProposedFact = z.infer<typeof ProposedFactSchema>;

/** What a bot sends to `propose_facts`, and what server code passes to `proposeFacts`. */
export const FactProposalInputSchema = z.object({
  facts: z.array(ProposedFactSchema).min(1).max(FACTS_PER_PROPOSAL_MAX),
  source: FactSourceSchema,
  /** The page URL or file name the facts were read from. */
  sourceRef: z.string().trim().max(500).optional(),
  /** A zone name; the Company zone when omitted. */
  zone: z.string().trim().min(1).max(60).optional(),
});
export type FactProposalInput = z.infer<typeof FactProposalInputSchema>;

/** A pending proposal as the owner's inbox shows it. */
export const FactProposalSchema = z.object({
  id: Id,
  botId: Id.nullable(),
  botName: z.string().nullable(),
  zoneName: z.string(),
  source: FactSourceSchema,
  sourceRef: z.string().nullable(),
  facts: z.array(ProposedFactSchema),
  proposedAt: z.string(),
});
export type FactProposal = z.infer<typeof FactProposalSchema>;

export const DecideFactProposalInputSchema = z.object({
  proposalId: Id,
  approve: z.boolean(),
  /** Indexes of the facts to save; every fact when omitted. */
  keep: z
    .array(z.number().int().min(0).max(FACTS_PER_PROPOSAL_MAX))
    .max(FACTS_PER_PROPOSAL_MAX)
    .optional(),
});
export type DecideFactProposalInput = z.infer<typeof DecideFactProposalInputSchema>;

/** A saved team directory entry. */
export const TeamDirectoryEntrySchema = DirectoryPersonSchema.extend({
  id: Id,
  emails: z.array(z.string()),
  source: FactSourceSchema.nullable(),
  sourceRef: z.string().nullable(),
  updatedAt: z.string(),
});
export type TeamDirectoryEntry = z.infer<typeof TeamDirectoryEntrySchema>;

export const TeamDirectorySchema = z.object({
  entries: z.array(TeamDirectoryEntrySchema),
  /** Whether the caller may edit it: Space admins only. */
  canManage: z.boolean(),
});
export type TeamDirectory = z.infer<typeof TeamDirectorySchema>;

/** Adds a person, or replaces the entry `id` names. */
export const SaveDirectoryEntryInputSchema = DirectoryPersonSchema.extend({ id: Id.optional() });
export type SaveDirectoryEntryInput = z.infer<typeof SaveDirectoryEntryInputSchema>;
